import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Alert
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";

const initialForm = {
  questionId: "",
  title: "",
  description: "",
  difficulty: "",
  parts: {
    part1: {
      description: "",
      maxMarks: ""
    },
    part2: {
      description: "",
      maxMarks: ""
    }
  },
  status: "ACTIVE"
};

const QuestionFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId") || "";
  const isEditMode = Boolean(id && id !== 'new');
  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [usedInLabs, setUsedInLabs] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.questionId.trim()) nextErrors.questionId = "Question ID is required.";
    if (!form.title.trim()) nextErrors.title = "Title is required.";
    if (!form.description.trim()) nextErrors.description = "Description is required.";
    if (!form.difficulty) nextErrors.difficulty = "Difficulty is required.";
    if (isEditMode && !form.status) nextErrors.status = "Status is required.";
    if (!form.parts.part1.description.trim()) {
      nextErrors.part1Description = "part1 description is required.";
    }
    if (Number(form.parts.part1.maxMarks) <= 0) {
      nextErrors.part1MaxMarks = "part1 max marks must be greater than 0.";
    }
    if (!form.parts.part2.description.trim()) {
      nextErrors.part2Description = "Node.js description is required.";
    }
    if (Number(form.parts.part2.maxMarks) <= 0) {
      nextErrors.part2MaxMarks = "Node.js max marks must be greater than 0.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const normalizeDifficulty = (value) => {
    if (!value) return "";
    const normalized = String(value).trim().toUpperCase();
    if (["EASY", "MEDIUM", "HARD"].includes(normalized)) {
      return normalized;
    }
    return value;
  };

  const normalizeStatus = (data) => {
    if (typeof data?.isActive === "boolean") {
      return data.isActive ? "ACTIVE" : "INACTIVE";
    }
    return data?.status || "ACTIVE";
  };

  React.useEffect(() => {
    let isMounted = true;

    const loadNextQuestionId = async () => {
      setLoading(true);
      setSubmitError("");
      try {
        const response = await axiosInstance.get("/questions");
        if (!isMounted) return;
        const data = response?.data?.data || response?.data || [];
        const maxSequence = (Array.isArray(data) ? data : []).reduce((max, question) => {
          const match = String(question?.questionId || "")
            .trim()
            .toUpperCase()
            .match(/^Q(\d+)$/);
          if (!match) return max;
          return Math.max(max, Number(match[1]));
        }, 0);
        const nextQuestionId = `Q${maxSequence + 1}`;
        setForm((prev) => ({ ...prev, questionId: nextQuestionId }));
      } catch (error) {
        if (isMounted) {
          setSubmitError(
            error?.response?.data?.message || "Unable to auto-generate question ID."
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const loadQuestion = async () => {
      setLoading(true);
      setSubmitError("");
      try {
        const response = await axiosInstance.get(`/questions/${id}`);
        if (!isMounted) return;

        const data = response?.data?.data || response?.data || {};
        setForm({
          questionId: data.questionId || data.id || "",
          title: data.title || "",
          description: data.description || "",
          difficulty: normalizeDifficulty(data.difficulty),
          parts: {
            part1: {
              description: data.parts?.part1?.description || "",
              maxMarks: data.parts?.part1?.maxMarks ?? ""
            },
            part2: {
              description: data.parts?.part2?.description || "",
              maxMarks: data.parts?.part2?.maxMarks ?? ""
            }
          },
          status: normalizeStatus(data)
        });
        setUsedInLabs(Boolean(data.usedInLab || data.usedInLabs));
      } catch (error) {
        if (isMounted) {
          setSubmitError(
            error?.response?.data?.message || "Unable to load question details."
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (isEditMode) {
      loadQuestion();
    } else {
      setForm(initialForm);
      setUsedInLabs(false);
      loadNextQuestionId();
    }

    return () => {
      isMounted = false;
    };
  }, [id, isEditMode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError("");

    try {
      if (isEditMode) {
        await axiosInstance.put(`/questions/${id}`, {
          title: form.title,
          description: form.description,
          difficulty: form.difficulty,
          parts: {
            part1: {
              description: form.parts.part1.description,
              maxMarks: Number(form.parts.part1.maxMarks)
            },
            part2: {
              description: form.parts.part2.description,
              maxMarks: Number(form.parts.part2.maxMarks)
            }
          },
          status: form.status
        });
        navigate("/faculty/questions");
        return;
      }

      await axiosInstance.post("/questions", {
        questionId: form.questionId,
        courseId,
        title: form.title,
        description: form.description,
        difficulty: form.difficulty,
        parts: {
          part1: {
            description: form.parts.part1.description,
            maxMarks: Number(form.parts.part1.maxMarks)
          },
          part2: {
            description: form.parts.part2.description,
            maxMarks: Number(form.parts.part2.maxMarks)
          }
        }
      });

      setForm(initialForm);
      navigate("/faculty/questions");
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message || "Unable to save question."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(initialForm);
    setErrors({});
    setSubmitError("");
    // navigate("/faculty/questions");
    navigate(-1)
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {isEditMode ? "Edit Question" : "Add Question"}
      </Typography>

      {usedInLabs && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This question is already used in active labs. Some changes may be restricted.
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Paper
        elevation={1}
        sx={{ p: 3, maxWidth: 720, opacity: loading ? 0.7 : 1 }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Stack spacing={2}>
          <TextField
            label="Question ID"
            value={form.questionId}
            onChange={handleChange("questionId")}
            error={Boolean(errors.questionId)}
            helperText={errors.questionId}
            fullWidth
            required
            disabled
          />

          <TextField
            label="Title"
            value={form.title}
            onChange={handleChange("title")}
            error={Boolean(errors.title)}
            helperText={errors.title}
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange("description")}
            error={Boolean(errors.description)}
            helperText={errors.description}
            fullWidth
            required
            multiline
            minRows={4}
          />

          <FormControl fullWidth required error={Boolean(errors.difficulty)}>
            <InputLabel id="question-difficulty">Difficulty</InputLabel>
            <Select
              labelId="question-difficulty"
              label="Difficulty"
              value={form.difficulty}
              onChange={handleChange("difficulty")}
            >
              <MenuItem value="EASY">Easy</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HARD">Hard</MenuItem>
            </Select>
            {errors.difficulty && (
              <FormHelperText>{errors.difficulty}</FormHelperText>
            )}
          </FormControl>

          <Divider />

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Part 1
          </Typography>

          <TextField
            label="part1 Description"
            value={form.parts.part1.description}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                parts: {
                  ...prev.parts,
                  part1: {
                    ...prev.parts.part1,
                    description: event.target.value
                  }
                }
              }))
            }
            error={Boolean(errors.part1Description)}
            helperText={errors.part1Description}
            fullWidth
            required
            multiline
            minRows={3}
          />

          <TextField
            label="part1 Max Marks"
            type="number"
            inputProps={{ min: 1 }}
            value={form.parts.part1.maxMarks}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                parts: {
                  ...prev.parts,
                  part1: {
                    ...prev.parts.part1,
                    maxMarks: event.target.value
                  }
                }
              }))
            }
            error={Boolean(errors.part1MaxMarks)}
            helperText={errors.part1MaxMarks}
            fullWidth
            required
          />

          <Divider />

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Part 2
          </Typography>

          <TextField
            label="Node.js Description"
            value={form.parts.part2.description}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                parts: {
                  ...prev.parts,
                  part2: {
                    ...prev.parts.part2,
                    description: event.target.value
                  }
                }
              }))
            }
            error={Boolean(errors.part2Description)}
            helperText={errors.part2Description}
            fullWidth
            required
            multiline
            minRows={3}
          />

          <TextField
            label="Node.js Max Marks"
            type="number"
            inputProps={{ min: 1 }}
            value={form.parts.part2.maxMarks}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                parts: {
                  ...prev.parts,
                  part2: {
                    ...prev.parts.part2,
                    maxMarks: event.target.value
                  }
                }
              }))
            }
            error={Boolean(errors.part2MaxMarks)}
            helperText={errors.part2MaxMarks}
            fullWidth
            required
          />

          {isEditMode && (
            <FormControl fullWidth required error={Boolean(errors.status)}>
              <InputLabel id="question-status">Status</InputLabel>
              <Select
                labelId="question-status"
                label="Status"
                value={form.status}
                onChange={handleChange("status")}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </Select>
              {errors.status && (
                <FormHelperText>{errors.status}</FormHelperText>
              )}
            </FormControl>
          )}

          <Divider />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="text" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {isEditMode ? "Save Changes" : "Submit"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default QuestionFormPage;
